import React, { useEffect, useState } from "react";
import { Badge, Modal, Nav, Spinner, Table } from "react-bootstrap";
import { rockAdminApi } from "../../services/rockAdminApi";
import ErrorAlert from "../common/ErrorAlert";

function Avatar({ src, name, size = 40 }) {
  if (src) {
    return (
      <img
        alt={name}
        src={src}
        style={{
          width: size,
          height: size,
          objectFit: "cover",
          borderRadius: "50%",
          border: "1px solid var(--tblr-border-color)",
          flexShrink: 0
        }}
      />
    );
  }
  const initials = (name || "?")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: "var(--tblr-primary)",
        color: "white",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 600,
        fontSize: size * 0.35,
        flexShrink: 0
      }}
    >
      {initials}
    </div>
  );
}

function DeletedBadge({ deletedAt }) {
  if (!deletedAt) return null;
  return (
    <Badge bg="danger" className="ms-1" style={{ fontSize: "0.65rem" }}>
      Deleted
    </Badge>
  );
}

function ThumbImg({ src }) {
  if (!src) return <div style={{ width: 36, height: 36, borderRadius: 4, background: "var(--tblr-bg-surface-secondary)" }} />;
  return (
    <img
      alt=""
      src={src}
      style={{
        width: 36,
        height: 36,
        objectFit: "cover",
        borderRadius: 4,
        border: "1px solid var(--tblr-border-color)"
      }}
    />
  );
}

function CollectionsTab({ collections }) {
  if (!collections?.length) {
    return <div className="text-muted text-center py-4">No collections.</div>;
  }
  return (
    <Table hover className="table-vcenter mb-0">
      <thead>
        <tr>
          <th>Name</th>
          <th style={{ width: 160 }}>Created</th>
          <th style={{ width: 80 }}>Status</th>
        </tr>
      </thead>
      <tbody>
        {collections.map((c) => (
          <tr key={c.id} className={c.deleted_at ? "text-muted" : ""}>
            <td className="fw-semibold">
              {c.name}
              <DeletedBadge deletedAt={c.deleted_at} />
            </td>
            <td className="small">{c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</td>
            <td>
              {c.deleted_at ? (
                <Badge bg="danger" style={{ fontSize: "0.65rem" }}>Deleted</Badge>
              ) : (
                <Badge bg="success" style={{ fontSize: "0.65rem", color: "white" }}>Active</Badge>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function MyRocksTab({ myRocks }) {
  if (!myRocks?.length) {
    return <div className="text-muted text-center py-4">No rocks.</div>;
  }
  return (
    <Table hover className="table-vcenter mb-0">
      <thead>
        <tr>
          <th style={{ width: 44 }}></th>
          <th>Name</th>
          <th>Type</th>
          <th style={{ width: 120 }}>Identified</th>
          <th style={{ width: 80 }}>Status</th>
        </tr>
      </thead>
      <tbody>
        {myRocks.map((r) => (
          <tr key={r.id} className={r.deleted_at ? "text-muted" : ""}>
            <td><ThumbImg src={r.user_photo_url || r.image_url} /></td>
            <td>
              <div className="fw-semibold">
                {r.name}
                {r.is_favorite && (
                  <span className="ms-1 text-warning" title="Favorite">★</span>
                )}
              </div>
              {r.common_names?.length > 0 && (
                <div className="text-muted small">{r.common_names.join(", ")}</div>
              )}
            </td>
            <td className="text-muted small">{r.type || "—"}</td>
            <td className="small">{r.identified_at ? new Date(r.identified_at).toLocaleDateString() : "—"}</td>
            <td>
              {r.deleted_at ? (
                <Badge bg="danger" style={{ fontSize: "0.65rem" }}>Deleted</Badge>
              ) : (
                <Badge bg="success" style={{ fontSize: "0.65rem", color: "white" }}>Active</Badge>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function RecentSnapsTab({ snaps }) {
  if (!snaps?.length) {
    return <div className="text-muted text-center py-4">No recent snaps.</div>;
  }
  return (
    <Table hover className="table-vcenter mb-0">
      <thead>
        <tr>
          <th style={{ width: 44 }}></th>
          <th>Name</th>
          <th>Type</th>
          <th style={{ width: 120 }}>Snapped</th>
          <th style={{ width: 80 }}>Status</th>
        </tr>
      </thead>
      <tbody>
        {snaps.map((s) => (
          <tr key={s.id} className={s.deleted_at ? "text-muted" : ""}>
            <td><ThumbImg src={s.user_photo_url} /></td>
            <td>
              <div className="fw-semibold">{s.name}</div>
              {s.alternative_names?.length > 0 && (
                <div className="text-muted small">{s.alternative_names.join(", ")}</div>
              )}
            </td>
            <td className="text-muted small">{s.type || "—"}</td>
            <td className="small">{s.snapped_at ? new Date(s.snapped_at).toLocaleDateString() : "—"}</td>
            <td>
              {s.deleted_at ? (
                <Badge bg="danger" style={{ fontSize: "0.65rem" }}>Deleted</Badge>
              ) : (
                <Badge bg="success" style={{ fontSize: "0.65rem", color: "white" }}>Active</Badge>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function UserDetailView({ userId }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);
  const [activeTab, setActiveTab] = useState("rocks");

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!userId) return;
      setLoading(true);
      setError(null);
      setDetail(null);
      try {
        const data = await rockAdminApi.getUserDetail(userId);
        if (!cancelled) setDetail(data);
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => { cancelled = true; };
  }, [userId]);

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" size="sm" className="me-2" />
        Loading...
      </div>
    );
  }
  if (error) return <ErrorAlert error={error} title="Failed to load user data" />;
  if (!detail) return null;

  const { user, synced_at, data } = detail;
  const collections = data?.collections ?? [];
  const myRocks = data?.my_rocks ?? [];
  const snaps = data?.recent_snaps ?? [];

  return (
    <div>
      {/* User header */}
      <div className="d-flex align-items-center gap-3 mb-4 p-3 rounded" style={{ background: "var(--tblr-bg-surface-secondary)" }}>
        <Avatar src={user?.avatar} name={user?.name} size={56} />
        <div className="flex-grow-1">
          <div className="fw-bold fs-5">{user?.name || "—"}</div>
          <div className="text-muted small">{user?.email || "—"}</div>
          {synced_at && (
            <div className="text-muted small mt-1">
              Last synced: {new Date(synced_at).toLocaleString()}
            </div>
          )}
        </div>
        <div className="d-flex gap-3 text-center">
          <div>
            <div className="fw-bold fs-4">{collections.length}</div>
            <div className="text-muted small">Collections</div>
          </div>
          <div>
            <div className="fw-bold fs-4">{myRocks.length}</div>
            <div className="text-muted small">My Rocks</div>
          </div>
          <div>
            <div className="fw-bold fs-4">{snaps.length}</div>
            <div className="text-muted small">Snaps</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Nav variant="tabs" activeKey={activeTab} onSelect={setActiveTab} className="mb-0">
        <Nav.Item>
          <Nav.Link eventKey="rocks">
            My Rocks
            <Badge bg="secondary" className="ms-2" style={{ fontSize: "0.65rem" }}>{myRocks.length}</Badge>
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="snaps">
            Recent Snaps
            <Badge bg="secondary" className="ms-2" style={{ fontSize: "0.65rem" }}>{snaps.length}</Badge>
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="collections">
            Collections
            <Badge bg="secondary" className="ms-2" style={{ fontSize: "0.65rem" }}>{collections.length}</Badge>
          </Nav.Link>
        </Nav.Item>
      </Nav>
      <div className="border border-top-0 rounded-bottom" style={{ overflowX: "auto" }}>
        {activeTab === "rocks" && <MyRocksTab myRocks={myRocks} />}
        {activeTab === "snaps" && <RecentSnapsTab snaps={snaps} />}
        {activeTab === "collections" && <CollectionsTab collections={collections} />}
      </div>
    </div>
  );
}

export default function AdminUserDetailModal({ show, onHide, user }) {
  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          {user ? `${user.name || user.email || `User #${user.user_id}`}` : "User Detail"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {user ? <UserDetailView userId={user.user_id} /> : null}
      </Modal.Body>
    </Modal>
  );
}
